import type {
  BaseLeagueFilters,
  League,
  LeagueDurationParams,
  LeagueInfo,
  LeagueProjectionAttributes,
  LeaguesList,
  LeaguesResponse
} from '@library/types/leagues';
import {
  getLeagueAttributes,
  hasStandAloneOwnAttributes
} from '@bo-utils/leagueAttributes';
import type { ComboboxOption } from '@library/components/selects';
import { groupBy } from 'ramda';

export const getLeaguesList = (
  leagueData?: LeaguesResponse
): LeaguesList | [] =>
  leagueData?.data
    .filter(({ attributes: { parent_id } }) => !parent_id)
    .map(({ id, attributes: { name, icon, active, image_url: imageUrl } }) => {
      return { id, active, icon, imageUrl, name };
    })
    .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

export const getLeagueOptions = (
  leagueData?: LeaguesResponse
): Array<ComboboxOption> =>
  leagueData?.data
    .map(({ id, attributes: { name } }) => {
      return { label: name, value: id };
    })
    .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

export const getLeagueInfo = ({
  leagueData,
  selectedLeagueId
}: {
  leagueData?: LeaguesResponse;
  selectedLeagueId?: League['id'];
}): LeagueInfo | undefined => {
  if (!leagueData?.data || !selectedLeagueId) {
    return undefined;
  }

  let selectedLeague: League | undefined;

  selectedLeague = leagueData.data.find(
    (league) => league.id === selectedLeagueId
  );

  if (!selectedLeague) {
    [selectedLeague] = leagueData.data;
  }

  const {
    id,
    attributes: { name, rank, active, icon, image_url: imageUrl }
  } = selectedLeague;

  const baseLeagueFilters = getLeagueAttributes({
    leagueData,
    selectedLeagueId: id
  });

  return {
    baseLeagueFilters,
    id,
    name,
    rank,
    active,
    icon,
    imageUrl
  };
};

export const getLeagueDurationAttributes = ({
  durationId,
  leagueInfo
}: LeagueDurationParams): BaseLeagueFilters => {
  const emptyResponse = {
    durations: [],
    leagueId: '',
    name: '',
    projectionTypes: [],
    positions: [],
    statTypes: []
  };
  if (!leagueInfo || !durationId) {
    return emptyResponse;
  }

  const { baseLeagueFilters, name } = leagueInfo;

  if (hasStandAloneOwnAttributes({ name })) {
    return baseLeagueFilters[0];
  }

  const leagueFilters = baseLeagueFilters.find((league) =>
    league.durations.some(
      (duration) => duration.leagueProjectionFilterId === durationId
    )
  );

  return leagueFilters || emptyResponse;
};

export const getBulkLeagueIdGroups = ({
  leagueData
}: {
  leagueData?: LeaguesResponse;
}): Array<Array<string>> => {
  if (!leagueData) {
    return [];
  }

  const syncLeagues = leagueData?.data?.filter((league) => {
    return league.attributes.parent_id && league.attributes.sync_to_parent;
  });

  const childLeaguesWithoutParents = groupBy(
    (league) => String(league.attributes.parent_id) || '',
    syncLeagues || []
  );

  const bulkLeagueIdGroups = Object.keys(childLeaguesWithoutParents).flatMap(
    (parentId) => {
      const childLeagues = childLeaguesWithoutParents[parentId];

      if (!childLeagues || childLeagues?.length === 0) {
        return [];
      }

      const childIds = childLeagues.map((league) => league.id);

      childIds?.push(parentId);

      return [childIds];
    }
  );

  return bulkLeagueIdGroups;
};

export const getBulkLeagueIds = ({
  bulkLeagueIdGroups,
  leagueId
}: {
  bulkLeagueIdGroups: Array<Array<string>>;
  leagueId?: string;
}): Array<string> =>
  bulkLeagueIdGroups.find((group) => group.includes(leagueId ?? '')) ?? [
    leagueId ?? ''
  ];

export const getDurationDisplayForLeague = ({
  durationId,
  leagueId,
  leagueInfo
}: {
  durationId: string;
  leagueId: string;
  leagueInfo?: LeagueInfo;
}): string | undefined => {
  const durationMatch = hasStandAloneOwnAttributes({ name: leagueInfo?.name });

  return durationMatch
    ? leagueInfo?.baseLeagueFilters
        .find((baseFilter) =>
          baseFilter.durations.some(
            (duration) => duration.leagueProjectionFilterId === durationId
          )
        )
        ?.durations.find(
          (duration) => duration.leagueProjectionFilterId === durationId
        )?.leagueProjectionFilterName
    : leagueInfo?.baseLeagueFilters.find(
        (baseFilter) => baseFilter.leagueId === leagueId
      )?.durations[0].leagueProjectionFilterName;
};

export const getDurationsWithRelatedIds = ({
  durations,
  isMoreThanParentRelation
}: {
  durations: Array<LeagueProjectionAttributes>;
  isMoreThanParentRelation?: boolean;
}): Array<LeagueProjectionAttributes> => {
  return durations.map((duration) => {
    const {
      leagueProjectionFilterId: durationId,
      leagueProjectionFilterName: durationName
    } = duration;

    if (durationName === 'Full') {
      const fullDurationRelatedIds = durations
        .filter((aDuration) =>
          aDuration.leagueProjectionFilterName.includes('1')
        )
        .map((filterDuration) => filterDuration.leagueProjectionFilterId);

      const leagueProjectionFilterRelatedIds: Array<string> = [
        ...fullDurationRelatedIds,
        durationId
      ];
      return {
        ...duration,
        leagueProjectionFilterRelatedIds
      };
    }

    const parentId = duration.leagueProjectionFilterParentId;

    const relatedIds = durations
      .filter(
        (aDuration) =>
          aDuration.leagueProjectionFilterId === durationId ||
          aDuration.leagueProjectionFilterParentId === Number(durationId) ||
          (aDuration.leagueProjectionFilterParentId === parentId &&
            aDuration.leagueProjectionFilterParentId) ||
          aDuration.leagueProjectionFilterId === String(parentId) ||
          (isMoreThanParentRelation &&
            durationName.includes('1') &&
            aDuration.leagueProjectionFilterName.includes('1'))
      )
      .map((filterDuration) => filterDuration.leagueProjectionFilterId);

    const fullDurationId = durations.find(
      (aDuration) => aDuration.leagueProjectionFilterName === 'Full'
    )?.leagueProjectionFilterId;

    return {
      ...duration,
      leagueProjectionFilterRelatedIds:
        durationName.includes('1') && fullDurationId
          ? [...relatedIds, fullDurationId]
          : relatedIds
    };
  });
};
